import { createContext } from 'react';
import { Group, ProjectByIdQuery } from '../types/graphqlTypes';

// @TOOD: replace by useFragment when not experimental anymore https://www.apollographql.com/docs/react/api/react/hooks-experimental
export type Annotations = ProjectByIdQuery['projectById']['state']['annotations'];
export type Calculations = ProjectByIdQuery['projectById']['calculations'];
export type VolumeCalculation = Extract<Required<Calculations[0]>, { __typename: 'VolumeCalculation' }>;
export type ProjectionCalculation = Extract<Required<Calculations[0]>, { __typename: 'ProjectionCalculation' }>;
export type AnnotationContextGroup = Group & {
  /**
   * A group's ordered children: annotations plus nested child groups (a FLOOR
   * group's ROOM folders). Nesting is bounded to exactly this one extra level —
   * a nested group's own `annotations` contains only annotations.
   */
  annotations: (Annotations[0] | AnnotationContextGroup)[];
};

export function isAnnotationContextGroup(item: AnnotationContextGroup | Annotations[0]): item is AnnotationContextGroup {
  return item != null && 'annotations' in item && Array.isArray(item.annotations);
}

/**
 * Every leaf annotation under a tree item, descending through groups AND
 * nested groups (a floor's ROOM folders). Group nodes themselves are never
 * collected. NOTE: a nested folder node's `type` is the GroupType string
 * 'ROOM', which collides with the ROOM annotation type — consumers of mixed
 * children must use this (or isAnnotationContextGroup) instead of `type`
 * checks to tell folders from annotations.
 */
export function collectLeafAnnotations(item: AnnotationContextGroup | Annotations[0], result: Annotations[0][] = []): Annotations[0][] {
  if (isAnnotationContextGroup(item)) {
    for (const child of item.annotations) collectLeafAnnotations(child, result);
  } else {
    result.push(item);
  }
  return result;
}

export type AnnotationContextType = {
  annotations: (AnnotationContextGroup | Annotations[0])[];
  annotationsByIdentifier: { [key: string]: Annotations[0] };
  calculationsByIdentifier: { [key: string]: Calculations };
};
export const AnnotationContext = createContext<AnnotationContextType>({
  annotations: [],
  annotationsByIdentifier: {},
  calculationsByIdentifier: {},
});
